import { blue, green, lightBlue, lightGreen, lime, orange, yellow } from '@mui/material/colors';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { useRef, useState } from 'react';
import { getElevationColor } from '../../utils';
import { useLayers } from './layers';
import { useSource } from './source';
const minSpeedMax = 5;
export const speeds = [{
  min: 30,
  color: orange[500]
}, {
  min: 25,
  color: yellow[500]
}, {
  min: 20,
  color: lime[500]
}, {
  min: 15,
  color: lightGreen[500]
}, {
  min: 10,
  color: green[500]
}, {
  min: minSpeedMax,
  color: lightBlue[500]
}, {
  min: 0,
  max: minSpeedMax,
  color: blue[500]
}];
const traceSourceId = 'route';
export const traceLayerId = 'route';
const traceWayPointsSourceId = 'way-points';
export const traceWayPointsLayerId = 'way-points';
const traceHighlightSourceId = 'trace-highlight';
export const traceHighlightLayerId = 'trace-highlight';
export function useTraces(map, {
  onHighlight
} = {}) {
  const [initialized, setInitialized] = useState(false);
  const {
    addGeoJSONSource: addRoutesSource,
    updateGeoJSONSource: updateRouteSource,
    clearGeoJSONSource: clearRouteSource
  } = useSource(map, traceSourceId);
  const {
    addGeoJSONSource: addWayPointsSource,
    updateGeoJSONSource: updateWayPointsSource,
    clearGeoJSONSource: clearWayPointSource
  } = useSource(map, traceWayPointsSourceId);
  const {
    addGeoJSONSource: addHighlightSource,
    updateGeoJSONSource: updateHighlightSource,
    clearGeoJSONSource: clearHighlightSource
  } = useSource(map, traceHighlightSourceId);
  const {
    addCircleLayer,
    addLineLayer
  } = useLayers(map);
  const initializedRef = useRef(false);
  const traceRef = useRef(null);
  const highlightedIndexRef = useRef(null);
  const onHighlightRef = useRef(onHighlight);
  const listenersRef = useRef(null);
  const hoverRef = useRef({});
  onHighlightRef.current = onHighlight;
  function init() {
    if (!map || initializedRef.current) return;
    addWayPointsSource();
    addRoutesSource(undefined, {
      type: 'geojson',
      lineMetrics: true
    });
    addHighlightSource();
    addCircleLayer(traceWayPointsLayerId, traceWayPointsSourceId, {
      'circle-radius': 6,
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 1,
      'circle-color': ['get', 'color'],
      'circle-stroke-color': '#fff'
    }, {
      filter: ['==', 'wayPoint', true]
    });
    addLineLayer(traceLayerId, traceSourceId, {
      'line-join': 'round',
      'line-cap': 'round'
    }, {
      'line-color': ['get', 'color'],
      'line-width': 7
    });
    addCircleLayer(traceHighlightLayerId, traceHighlightSourceId, {
      'circle-radius': 8,
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 1,
      'circle-color': '#fff',
      'circle-opacity': 0.5,
      'circle-stroke-color': '#326ac2'
    }, {
      disableCursorChange: true
    });
    if (onHighlightRef.current) initHoverListeners();
    initializedRef.current = true;
    setInitialized(true);
  }
  function initHoverListeners() {
    if (!map || listenersRef.current) return;
    const listeners = {
      mousemove: ({
        lngLat
      }) => {
        map.getCanvas().style.cursor = 'pointer';
        hoverRef.current.lngLat = lngLat;
        if (hoverRef.current.frame !== undefined) return;
        hoverRef.current.frame = requestAnimationFrame(() => {
          hoverRef.current.frame = undefined;
          const trace = traceRef.current;
          const lngLat = hoverRef.current.lngLat;
          if (!trace || !lngLat) return;
          const {
            properties
          } = nearestPointOnLine(trace.geometry, [lngLat.lng, lngLat.lat]);
          if (typeof properties?.index === 'number') onHighlightRef.current?.(properties.index);
        });
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = '';
        cancelHover();
        onHighlightRef.current?.(null);
      }
    };
    map.on('mousemove', traceLayerId, listeners.mousemove);
    map.on('mouseleave', traceLayerId, listeners.mouseleave);
    listenersRef.current = listeners;
  }
  function cancelHover() {
    if (hoverRef.current.frame !== undefined) cancelAnimationFrame(hoverRef.current.frame);
    hoverRef.current = {};
  }
  function addWayPoints(trace) {
    const features = [];
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: trace.geometry.coordinates[0]
      },
      properties: {
        color: '#2ac682',
        wayPoint: true,
        movable: false
      }
    });
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: trace.geometry.coordinates[trace.geometry.coordinates.length - 1]
      },
      properties: {
        color: '#dd428d',
        wayPoint: true,
        movable: false
      }
    });
    updateWayPointsSource({
      type: 'FeatureCollection',
      features
    });
  }
  function update(trace, distances, slopePercentages, colorScale) {
    if (!map) return;
    clearTrace();
    traceRef.current = trace;
    addWayPoints(trace);
    const {
      geometry,
      speeds,
      elevations
    } = trace;
    const {
      coordinates
    } = geometry;
    const features = [];
    if (colorScale === 'speed' && speeds && speeds.length === coordinates.length || colorScale === 'elevations' && elevations && elevations.length === coordinates.length) {
      features.push({
        type: 'Feature',
        geometry,
        properties: {
          color: 'black'
        }
      });
      const totalDistance = distances[distances.length - 1];
      map.setPaintProperty(traceLayerId, 'line-gradient', ['interpolate', ['linear'], ['line-progress'], ...distances.reduce((res, distance, index) => {
        if (index === 0 || distance > distances[index - 1]) res.push(distance / totalDistance, colorScale === 'elevations' ? getElevationColor(slopePercentages[index]) : getSpeedColor(speeds[index]));
        return res;
      }, [])]);
    } else {
      features.push({
        type: 'Feature',
        geometry,
        properties: {
          color: '#326ac2'
        }
      });
      map.setPaintProperty(traceLayerId, 'line-gradient', ['interpolate', ['linear'], ['line-progress'], 0, '#326ac2']);
    }
    updateRouteSource({
      type: 'FeatureCollection',
      features
    });
    highlight(highlightedIndexRef.current);
  }
  function highlight(index) {
    highlightedIndexRef.current = index;
    const coordinates = index !== null && traceRef.current?.geometry.coordinates[index];
    if (!coordinates) {
      clearHighlightSource();
      return;
    }
    updateHighlightSource({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates
        },
        properties: {}
      }]
    });
  }
  function clearTrace() {
    clearWayPointSource();
    clearRouteSource();
    clearHighlightSource();
  }
  function getSpeedColor(speed) {
    return speeds.find(({
      min
    }) => speed >= min)?.color || speeds[speeds.length - 1].color;
  }
  function destroy() {
    clearTrace();
    cancelHover();
    if (map && listenersRef.current) {
      map.off('mousemove', traceLayerId, listenersRef.current.mousemove);
      map.off('mouseleave', traceLayerId, listenersRef.current.mouseleave);
    }
    listenersRef.current = null;
    traceRef.current = null;
    highlightedIndexRef.current = null;
    initializedRef.current = false;
    setInitialized(false);
  }
  return {
    initialized,
    init,
    update,
    highlight,
    clear: clearTrace,
    destroy
  };
}